import { X, CheckCircle2, Fingerprint } from 'lucide-react';
import { useTheme, THEMES } from '../contexts/ThemeContext';

interface BrandSettingsModalProps {
  onClose: () => void;
}

export function BrandSettingsModal({ onClose }: BrandSettingsModalProps) {
  const { activeTheme, setTheme } = useTheme();

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-200">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-2 text-slate-900">
            <Fingerprint size={16} />
            <h2 className="text-[15px] font-bold font-display">
              Brand Identity Configuration
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500 font-bold mb-4">
            Active Signal Accent
          </p>
          
          <div className="space-y-3">
            {Object.values(THEMES).map((theme) => {
              const isActive = activeTheme.id === theme.id;
              
              return (
                <button
                  key={theme.id}
                  onClick={() => setTheme(theme.id as any)}
                  className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all ${
                    isActive 
                      ? 'border-slate-900 bg-slate-50 shadow-sm' 
                      : 'border-slate-200 bg-white hover:border-slate-400 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    {/* Color Swatch */}
                    <div 
                      className="w-8 h-8 rounded-lg border border-slate-200/50 shadow-sm flex items-center justify-center"
                      style={{ backgroundColor: theme.colors.primary }}
                    >
                      {isActive && <CheckCircle2 size={16} className="text-white" />}
                    </div>
                    
                    {/* Text Details */}
                    <div className="text-left">
                      <p className={`text-sm font-bold font-display ${isActive ? 'text-slate-900' : 'text-slate-700'}`}>
                        {theme.name}
                      </p>
                      <p className="text-[12px] text-slate-500 mt-0.5 leading-relaxed">
                        {theme.description}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          
          {/* Example Output Box to preview changes instantly */}
          <div className="mt-6 p-4 rounded-xl border border-slate-200 bg-white">
             <p className="text-[10px] font-mono text-slate-400 mb-3">PREVIEW: ACTIVE COMPONENT</p>
             <div className="flex items-center justify-between">
               <span className="text-sm font-medium text-slate-700">Deploy Changes</span>
               <div 
                 className="px-4 py-2 rounded-lg text-white text-xs font-bold shadow-sm transition-colors duration-300"
                 style={{ backgroundColor: activeTheme.colors.primary }}
               >
                 Extract Signal
               </div>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
}